import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DrillResponse, DrillImageMetadata, DrillVideoMetadata, TrainingDrill } from './Model';
import {
  DrillCard,
  DrillDetailModal,
  DrillToolbar,
  DrillFilters,
} from './Drills';
import { restClient } from './RestClient';
import { AuthService } from './AuthService';
import { useReferenceData } from './ReferenceDataContext';

interface TrainingDrillsProps {
  onError?: (message: string) => void;
}

export const TrainingDrills: React.FC<TrainingDrillsProps> = ({ onError }) => {
  const location = useLocation();
  const [drillResponses, setDrillResponses] = useState<DrillResponse[]>([]);
  const [filteredDrills, setFilteredDrills] = useState<TrainingDrill[]>([]);
  const [selectedDrill, setSelectedDrill] = useState<TrainingDrill | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  const drills = useMemo(() => drillResponses.map(r => r.drill), [drillResponses]);
  const favouriteDrillIds = useMemo(() => new Set(drillResponses.filter(r => r.isFavourite).map(r => r.drill.id)), [drillResponses]);
  const imagesByDrillId = useMemo(() => {
    const map = new Map<number, DrillImageMetadata[]>();
    drillResponses.forEach(r => { if (r.images?.length > 0) map.set(r.drill.id, r.images); });
    return map;
  }, [drillResponses]);
  const videoByDrillId = useMemo(() => {
    const map = new Map<number, DrillVideoMetadata>();
    drillResponses.forEach(r => { if (r.video) map.set(r.drill.id, r.video); });
    return map;
  }, [drillResponses]);

  const isAdmin = AuthService.isAdmin();
  const isSuperUser = AuthService.isSuperUser();
  const { currentSport } = useReferenceData();

  // Check for success message from navigation state
  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);
      // Clear the message from state
      window.history.replaceState({}, document.title);
      // Auto-hide after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const result = await restClient.getDrills(currentSport, showHidden);

      if (result.IsSuccess) {
        setDrillResponses(result.Value);
        setFilteredDrills(result.Value.map(r => r.drill));
      } else {
        console.error('Error fetching drills:', result.Error);
        onError?.(result.Error?.description ?? 'Failed to load drills. Please try again.');
      }

      setIsLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSport, showHidden]);

  const handleToggleFavourite = useCallback(async (drillId: number) => {
    // Optimistic update
    setDrillResponses(prev =>
      prev.map(r => r.drill.id === drillId ? { ...r, isFavourite: !r.isFavourite } : r)
    );

    const result = await restClient.toggleDrillFavourite(drillId);
    if (result.IsSuccess) {
      // Reconcile with server state
      setDrillResponses(prev =>
        prev.map(r => r.drill.id === drillId ? { ...r, isFavourite: result.Value.isFavourite } : r)
      );
    } else {
      console.error('Error toggling favourite:', result.Error);
      onError?.(result.Error?.description ?? 'Failed to update favourite. Please try again.');
      // Revert on error
      setDrillResponses(prev =>
        prev.map(r => r.drill.id === drillId ? { ...r, isFavourite: !r.isFavourite } : r)
      );
    }
  }, [onError]);

  const handleFilteredDrillsChange = useCallback((filtered: TrainingDrill[]) => {
    setFilteredDrills(filtered);
  }, []);

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body loading-state">
          <p className="text-muted text-lg m-0">Loading drills...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible mb-4">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="btn-close" aria-label="Close message">
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">Training Drills Library</h2>
        <p className="page-description">
          Browse and discover training drills for your sessions. Filter by difficulty or category to find the perfect drill.
        </p>
      </div>

      {/* Toolbar */}
      <DrillToolbar isAdmin={isAdmin} isSuperUser={isSuperUser} />

      {/* Filters */}
      <DrillFilters
        drills={drills}
        onFilteredDrillsChange={handleFilteredDrillsChange}
        favouriteDrillIds={favouriteDrillIds}
        isAdmin={isAdmin}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
      />

      {/* Drills Grid */}
      <div className="drills-grid">
        {filteredDrills.map(drill => (
          <DrillCard
            key={drill.id}
            drill={drill}
            images={imagesByDrillId.get(drill.id)}
            video={videoByDrillId.get(drill.id)}
            onClick={() => setSelectedDrill(drill)}
            isAdmin={isAdmin}
            isFavourite={favouriteDrillIds.has(drill.id)}
            onToggleFavourite={handleToggleFavourite}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredDrills.length === 0 && drills.length > 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <p className="empty-state-text">No drills match your filters</p>
          <p className="text-muted text-sm mt-2 mb-4">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* No Drills at All State */}
      {drills.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p className="empty-state-text">No drills in the library yet</p>
          <p className="text-muted text-sm mt-2 mb-4">Add your first drill to get started</p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDrill && (
        <DrillDetailModal drill={selectedDrill} images={imagesByDrillId.get(selectedDrill.id)} video={videoByDrillId.get(selectedDrill.id)} onClose={() => setSelectedDrill(null)} />
      )}
    </div>
  );
};
